import { motion } from "framer-motion";

interface SameAsPrimaryToggleProps {
  /** Label text for the toggle */
  label: string;
  /** Whether the toggle is checked */
  checked: boolean;
  /** Callback when toggle state changes */
  onChange: (checked: boolean) => void;
}

/**
 * SameAsPrimaryToggle Component
 *
 * Checkbox toggle for co-insured driver scenarios.
 * Allows users to quickly apply the primary driver's information
 * to a co-insured driver without re-entering fields.
 *
 * Simple checkbox + label layout with smooth animations.
 *
 * @example
 * ```tsx
 * const [isSameAsPrimary, setIsSameAsPrimary] = useState(false);
 *
 * <SameAsPrimaryToggle
 *   label="Co-insured driver has same address as primary"
 *   checked={isSameAsPrimary}
 *   onChange={setIsSameAsPrimary}
 * />
 * ```
 */
export function SameAsPrimaryToggle({
  label,
  checked,
  onChange,
}: SameAsPrimaryToggleProps) {
  return (
    <motion.label
      className="same-as-primary cursor-pointer select-none hover:bg-gray-100 transition-colors"
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="w-4 h-4 rounded border-gray-300 text-brand-primary focus:ring-brand-primary cursor-pointer"
      />
      <span className="text-sm font-medium text-gray-700">{label}</span>
    </motion.label>
  );
}
